import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { 
  Email as EmailIcon, 
  Phone as PhoneIcon, 
  LocationOn as LocationIcon 
} from '@mui/icons-material';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would send this data to your backend

    setOpenSnackbar(true);
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const contactInfo = [
    {
      icon: <EmailIcon fontSize="large" color="primary" />,
      title: 'Correo Electrónico',
      content: 'info@facto.com.py',
      link: 'mailto:info@facto.com.py'
    },
    {
      icon: <PhoneIcon fontSize="large" color="primary" />,
      title: 'Teléfono',
      content: '+595 985298994',
      link: 'tel:+595985298994'
    },
    {
      icon: <LocationIcon fontSize="large" color="primary" />,
      title: 'Dirección',
      content: 'Maneco Galeano 871, Asunción, PY',
      link: 'https://maps.google.com/?q=123+Financial+District,+New+York,+NY+10005'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Contáctanos
      </Typography>
      
      <Typography variant="h6" paragraph align="center" color="textSecondary" sx={{ mb: 6 }}>
        ¿Tienes preguntas sobre Facto? ¡Estamos aquí para ayudar!
      </Typography>
      
      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Ponte en Contacto
            </Typography>
            <Typography paragraph color="textSecondary">
              Nuestro equipo está listo para responder tus preguntas y ayudarte a comenzar con Facto.
            </Typography>
            
            <Box sx={{ mt: 4 }}>
              {contactInfo.map((info, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {info.icon}
                    <Typography variant="h6" sx={{ ml: 2 }}>
                      {info.title}
                    </Typography>
                  </Box>
                  <Typography 
                    component="a" 
                    href={info.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ 
                      ml: 5, 
                      textDecoration: 'none',
                      color: 'text.primary',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: 'primary.main'
                      }
                    }}
                  >
                    {info.content}
                  </Typography>
                  {index < contactInfo.length - 1 && (
                    <Divider sx={{ my: 3 }} />
                  )}
                </Box>
              ))}
            </Box>
            
            <Typography variant="subtitle1" sx={{ mt: 4 }}>
              Horario de Atención:
            </Typography>
            <Typography color="textSecondary">
              Lunes - Viernes: 8:00 AM - 5:00 PM PYT
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Envíanos un Mensaje
            </Typography>
            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="name"
                    label="Tu Nombre"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    id="email"
                    label="Tu Correo Electrónico"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="subject"
                    label="Asunto"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    id="message"
                    label="Tu Mensaje"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={{ mt: 3 }}
              >
                Enviar Mensaje
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          ¡Tu mensaje ha sido enviado exitosamente! Te responderemos pronto.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContactPage;
